/**
 * Errors signalling that the LLM passed invalid arguments to a tool.
 *
 * Recovery strategy: the LLM should retry the call with corrected
 * arguments based on the error's `suggestions` and `details`.
 */

import { type ErrorRecoveryStrategy, ToolExecutionError } from './base.ts';

export abstract class InvalidToolInputError extends ToolExecutionError {
  readonly recovery: ErrorRecoveryStrategy = 'retry-with-different-input';
}

/**
 * Shared LLM-targeted suggestions for any error that boils down to "you
 * referenced a column / dataset name that doesn't exist on the input."
 * Populates the available list and the case-sensitivity reminder.
 */
function availableColumnsSuggestions(availableColumns: readonly string[]): readonly string[] {
  return [`Available columns: ${availableColumns.join(', ')}.`, 'Column names are case-sensitive.'];
}

/**
 * A dataset (by name or by external ID) referenced in tool input was not
 * registered by any earlier tool call.
 */
export class DatasetNotFoundError extends InvalidToolInputError {
  constructor(
    readonly requested: string,
    readonly available: readonly string[],
  ) {
    super(`Dataset "${requested}" not found.`);
  }

  override suggestions(): readonly string[] {
    if (this.available.length === 0) {
      return [
        'No datasets are registered yet. Call a producer tool first (e.g. queryKnowledgeGraph, fetchTimeseriesDatapoints, registerDataset).',
      ];
    }
    return [`Available datasets: ${this.available.join(', ')}.`, 'Dataset names are case-sensitive.'];
  }

  override details() {
    return { requested: this.requested, available: this.available };
  }
}

/** A view (data-modeling view) referenced in tool input was not found. */
export class ViewNotFoundError extends InvalidToolInputError {
  constructor(
    readonly requested: string,
    readonly available: readonly string[],
  ) {
    super(`View "${requested}" not found.`);
  }

  override suggestions(): readonly string[] {
    if (this.available.length === 0) {
      return ['No views are configured for this workspace.'];
    }
    const preview = this.available.slice(0, 20).join(', ');
    const ellipsis = this.available.length > 20 ? `, … (${this.available.length} total)` : '';
    return [`Available views: ${preview}${ellipsis}.`];
  }

  override details() {
    return { requested: this.requested, available: this.available };
  }
}

/**
 * One or more column names referenced in tool input do not exist on the
 * input dataset. Raised by select / sort / groupBy / pivot / fold / spread /
 * unroll / expandTimeseries / join operators.
 *
 * The optional `operationHint` carries an operator-specific explanation
 * that's prepended to the standard suggestions. Useful when the column
 * absence has a non-obvious cause — e.g. a reshape operator earlier in
 * the pipeline dropped it, and the bare "available columns" list would
 * mislead the agent into thinking it was a typo.
 */
export class ColumnNotFoundError extends InvalidToolInputError {
  constructor(
    /** Operator that was being executed (e.g. `select`, `sort`, `join`, `expandTimeseries`). */
    readonly operation: string,
    readonly missingColumns: readonly string[],
    readonly availableColumns: readonly string[],
    readonly operationHint?: string,
  ) {
    const plural = missingColumns.length === 1 ? '' : 's';
    super(`${operation}: column${plural} not found: ${missingColumns.map((c) => `"${c}"`).join(', ')}.`);
  }

  override suggestions(): readonly string[] {
    const hint = this.operationHint === undefined ? [] : [this.operationHint];
    return [
      ...hint,
      ...availableColumnsSuggestions(this.availableColumns),
      'For nested fields, use dot notation (e.g. `metadata.unit`). Bracket access (`row["foo.bar"]`) is equivalent — both look up the literal property `foo.bar` on the row, not a path traversal.',
    ];
  }

  override details() {
    return {
      operation: this.operation,
      missingColumns: this.missingColumns,
      availableColumns: this.availableColumns,
      ...(this.operationHint === undefined ? {} : { operationHint: this.operationHint }),
    };
  }
}
/**
 * A derive expression evaluated to null for every row of a non-trivial input.
 * Two failure shapes share this error so the agent sees one consistent
 * signal whenever a derive's output column is unusable:
 *
 *  - **Silent-undefined** (no per-row errors collected) — the expression
 *    resolved cleanly per row but to undefined. Canonical cases:
 *    - `row["wellString.externalId"]` when the row has `wellString` (an
 *      object) rather than a flat property of that name.
 *    - A timeseries fetcher that returned empty datapoints for every
 *      externalId, then `last([])` / `value(undefined)` cascading to null.
 *    - An array reducer over an array intermediate that was empty in every
 *      row.
 *
 *  - **Per-row evaluation errors throughout** (`sampleErrors` populated) —
 *    every row's evaluation threw the same kind of error (e.g.
 *    `sumRunning is not a function`, `strlen expected a string, got
 *    number`). Per-row errors are otherwise collected and downgraded to a
 *    "N rows failed" warning that the tool layer drops; surfacing them
 *    here makes the actual underlying message visible to the agent.
 *
 * Distinct from {@link ColumnNotFoundError}: that fires at validation time
 * when a column reference is statically unresolvable. This fires at
 * post-evaluation time when references resolved fine but the evaluation
 * itself yielded only nulls.
 */
export class DerivedColumnAllNullError extends InvalidToolInputError {
  constructor(
    readonly deriveName: string,
    readonly expression: string,
    readonly rowCount: number,
    readonly availableColumns: readonly string[],
    readonly referencedColumns: readonly string[],
    /**
     * Up to `MAX_SAMPLE_EVAL_ERRORS` per-row evaluation errors collected
     * before this throw. Empty when the failure was silent-undefined.
     */
    readonly sampleErrors: readonly { row: number; error: string }[] = [],
  ) {
    const errBlock =
      sampleErrors.length > 0
        ? ` Per-row evaluation errors (${sampleErrors.length} sampled): ${sampleErrors
            .map((e) => `row ${e.row}: ${e.error}`)
            .join('; ')}.`
        : ' (No per-row errors collected — the expression silently produced undefined.)';
    super(`derive: column "${deriveName}" evaluated to null for all ${rowCount} rows.${errBlock}`);
  }

  override suggestions(): readonly string[] {
    const refList =
      this.referencedColumns.length > 0 ? this.referencedColumns.map((c) => `"${c}"`).join(', ') : '(none)';
    const out: string[] = [`Expression: \`${this.expression}\`.`];
    if (this.sampleErrors.length > 0) {
      out.push(
        'The expression threw the same error on every row — the message above shows what went wrong (function name typo, type mismatch, missing column on the row value, etc.). Fix the expression and retry.',
      );
    } else {
      out.push(
        'The expression resolved without throwing but produced undefined for every row — check that the referenced columns carry non-null values and that any fetchers / array reducers return non-empty data.',
      );
    }
    out.push(
      `Columns referenced by the expression: ${refList}.`,
      'For ref<entity> columns (e.g. `wellString`), use member access `wellString.externalId` — `row["wellString.externalId"]` is equivalent to `row.wellString.externalId` (a literal-key lookup), not a path traversal.',
      'For timeseries fetchers (`datapoints`, `hourly`, `daily`, `weekly`, `monthly`), confirm the referenced column is a `ref<ts>` and that the externalIds resolve to series with datapoints in CDF.',
      ...availableColumnsSuggestions(this.availableColumns),
    );
    return out;
  }

  override details() {
    return {
      deriveName: this.deriveName,
      expression: this.expression,
      rowCount: this.rowCount,
      referencedColumns: this.referencedColumns,
      availableColumns: this.availableColumns,
      sampleErrors: this.sampleErrors,
    };
  }
}

/**
 * A column reference is structurally invalid (wrong type, empty, conflicting,
 * or used with an incompatible operator). Distinct from `ColumnNotFoundError`
 * in that the column may exist — but the way it's referenced is wrong.
 */
export class InvalidColumnReferenceError extends InvalidToolInputError {
  constructor(
    message: string,
    /** Column that was referenced. */
    readonly column: string,
    /** Optional one-line hint pointing to the fix. */
    readonly hint?: string,
  ) {
    super(message);
  }

  override suggestions(): readonly string[] {
    return this.hint ? [this.hint] : [];
  }

  override details() {
    return { column: this.column };
  }
}

/** A `limit` value passed to an operator is out of range or wrong type. */
export class InvalidLimitError extends InvalidToolInputError {
  constructor(
    readonly limit: number,
    readonly reason: string,
  ) {
    super(`Invalid limit value: ${limit}. ${reason}`);
  }

  override suggestions(): readonly string[] {
    return ['Use a positive integer between 1 and 10000.'];
  }

  override details() {
    return { limit: this.limit };
  }
}

/**
 * The tool input was syntactically valid Zod-wise, but semantically incomplete
 * or contradictory (e.g. dashboard spec missing required panels, expandTimeseries
 * spec missing splitBy when needed, missing outputDataset key, etc.).
 *
 * Use this when the failure is a one-off spec validation that doesn't warrant
 * its own dedicated class. Pass tailored suggestions and details from the
 * call site.
 */
export class InvalidSpecError extends InvalidToolInputError {
  readonly #suggestions: readonly string[];
  readonly #details: Readonly<Record<string, unknown>>;

  constructor(
    message: string,
    opts: { suggestions?: readonly string[]; details?: Readonly<Record<string, unknown>> } = {},
  ) {
    super(message);
    this.#suggestions = opts.suggestions ?? [];
    this.#details = opts.details ?? {};
  }

  override suggestions(): readonly string[] {
    return this.#suggestions;
  }

  override details() {
    return this.#details;
  }
}

// Source-reference filter errors (knowledge-graph queries).

/** A source-reference filter referenced a column that doesn't exist on the input. */
export class SourceRefColumnMissingError extends InvalidToolInputError {
  constructor(
    readonly column: string,
    readonly availableColumns: readonly string[],
  ) {
    super(`Source-reference filter references unknown column "${column}".`);
  }

  override suggestions(): readonly string[] {
    return availableColumnsSuggestions(this.availableColumns);
  }

  override details() {
    return { column: this.column, availableColumns: this.availableColumns };
  }
}

/** A source-reference filter referenced a column whose type is incompatible. */
export class SourceRefTypeMismatchError extends InvalidToolInputError {
  constructor(
    readonly column: string,
    readonly expectedType: string,
    readonly actualType: string,
  ) {
    super(`Source-reference filter on column "${column}" expects type ${expectedType} but column is ${actualType}.`);
  }

  override suggestions(): readonly string[] {
    return [
      'Source-reference filters require columns of type "reference" or "directRelation".',
      'Use a different column or transform the source dataset to expose a reference column.',
    ];
  }

  override details() {
    return { column: this.column, expectedType: this.expectedType, actualType: this.actualType };
  }
}

/** A source-reference filter resolved to an empty set of source rows. */
export class SourceRefEmptyError extends InvalidToolInputError {
  constructor(readonly column: string) {
    super(`Source-reference filter on column "${column}" yielded no rows from the source dataset.`);
  }

  override suggestions(): readonly string[] {
    return [
      'Verify the source dataset is non-empty.',
      'Verify upstream filters did not remove all rows before the source-reference filter ran.',
    ];
  }

  override details() {
    return { column: this.column };
  }
}

/** A source-reference filter used an operator that is not supported in this position. */
export class SourceRefInvalidOpError extends InvalidToolInputError {
  constructor(
    readonly operator: string,
    readonly allowedOperators: readonly string[],
  ) {
    super(`Operator "${operator}" is not allowed in a source-reference filter.`);
  }

  override suggestions(): readonly string[] {
    return [`Allowed operators: ${this.allowedOperators.join(', ')}.`];
  }

  override details() {
    return { operator: this.operator, allowedOperators: this.allowedOperators };
  }
}

/**
 * Two or more source-reference filters target the same column with conflicting
 * specs (e.g. one expects strings, another expects directRelations).
 */
export class SourceRefConflictError extends InvalidToolInputError {
  constructor(
    readonly column: string,
    readonly conflictDescription: string,
  ) {
    super(`Source-reference filter conflict on column "${column}": ${conflictDescription}.`);
  }

  override suggestions(): readonly string[] {
    return ['Remove one of the conflicting filters or harmonize their specs.'];
  }

  override details() {
    return { column: this.column, conflictDescription: this.conflictDescription };
  }
}
